'use client';
// Correspondance ISO 15189 (F1) — quelle clause, quel écran, quelle couverture.
//
// Double usage, et c'est ce qui en fait la valeur :
//   • formation — un technicien voit à quoi sert chaque écran, et pourquoi ;
//   • préparation d'audit — « montrez-moi comment vous répondez au §6.5 » se
//     règle en un clic, y compris quand la réponse est « nous ne le couvrons
//     pas encore ».
//
// L'écran dit AUSSI ce qui manque. Un tableau de couverture qui n'afficherait
// que le vert servirait à se rassurer, pas à préparer un audit.
import React, { useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import {
  ISO15189, COUVERTURE_META, DIVERGENCES, NUMEROTATION_VERIFIEE, bilanCouverture,
  type Couverture,
} from '@/shared/iso15189';
import { TitrePage, I, card, EmptyState, FilterBar } from '@/components/ui';
import { Texte } from '@/components/Norme';
import type { GedView } from '@/lib/types';

import { Bouton } from '@/shared/Bouton';
const TONS: Record<Couverture, { bg: string; fg: string }> = {
  bon: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  partiel: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  faible: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  manquant: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  /* ⚠️ NI ROUGE NI VERT. Le rouge accuserait l'outil d'une lacune qu'il n'a
     pas ; le vert affirmerait une couverture qu'il ne fournit pas. Un ton
     neutre dit ce qui est : la question se règle ailleurs. */
  horsperimetre: { bg: 'var(--gray-50)', fg: 'var(--text-body)' },
};

const CHAPITRES: Record<number, string> = {
  4: 'Exigences générales',
  5: 'Structure et gouvernance',
  6: 'Ressources',
  7: 'Processus',
  8: 'Système de management',
};

export function Iso() {
  const { go, openSettings } = useGed();
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState<'toutes' | Couverture | 'avalider'>('toutes');
  const [ouverte, setOuverte] = useState<string | null>(null);
  const bilan = bilanCouverture();

  const liste = useMemo(() => {
    const texte = q.trim().toLowerCase();
    return ISO15189.filter((c) => {
      if (filtre === 'avalider' ? c.valide : filtre !== 'toutes' && c.couverture !== filtre) return false;
      if (!texte) return true;
      return [c.ref, c.titre, c.exigence, c.pourquoi, c.chezVous, ...c.ecrans.map((e) => e.label)]
        .join(' ').toLowerCase().includes(texte);
    });
  }, [q, filtre]);

  const tuiles: { v: number; l: string; k: typeof filtre; rouge?: boolean }[] = [
    { v: bilan.total, l: 'Articles suivis', k: 'toutes' },
    { v: bilan.par.bon, l: 'Couverts', k: 'bon' },
    { v: bilan.par.partiel + bilan.par.faible, l: 'Partiels ou faibles', k: 'partiel' },
    { v: bilan.par.manquant, l: 'Non couverts', k: 'manquant', rouge: true },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <TitrePage eyebrow="Norme · référentiel interne" titre="Correspondance ISO 15189"
        sousTitre={<>
          Pour chaque article : ce qu’il exige, pourquoi, l’écran qui y répond, et l’état de
          cette réponse. Sert à comprendre ce qu’on fait — et à montrer, en audit, où se trouve
          la preuve.
        </>} />

      {/* Deux réserves qui doivent précéder toute lecture du tableau. */}
      <div style={{ display: 'grid', gap: 10 }}>
        {!NUMEROTATION_VERIFIEE && (
          <div role="note" style={{ display: 'flex', gap: 10, background: 'var(--amber-50)', color: 'var(--amber-700)', borderRadius: 'var(--radius-md)', padding: '11px 14px', fontSize: 12.5, lineHeight: 1.55 }}>
            <I n="alert" s={16} style={{ flex: 'none', marginTop: 1 }} />
            <span>
              <strong>Numérotation à confronter à l’exemplaire du laboratoire.</strong> Les
              références sont proposées de mémoire : une référence fausse dans un outil
              pédagogique décrédibilise le reste. À vérifier avant de s’en servir en audit.
            </span>
          </div>
        )}
        {bilan.aValider > 0 && (
          <div role="note" style={{ display: 'flex', gap: 10, background: 'var(--cyan-50)', color: 'var(--cyan-700)', borderRadius: 'var(--radius-md)', padding: '11px 14px', fontSize: 12.5, lineHeight: 1.55 }}>
            <I n="pen" s={16} style={{ flex: 'none', marginTop: 1 }} />
            <span>
              <strong>{bilan.aValider} article(s) en rédaction proposée.</strong> Les commentaires
              engagent l’interprétation du laboratoire : ils restent marqués « non validés » tant
              que le RQ ne les a pas relus.
            </span>
          </div>
        )}
      </div>

      <div className="ged-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {tuiles.map((t) => {
          const actif = filtre === t.k;
          return (
            <button key={t.l} onClick={() => setFiltre(actif ? 'toutes' : t.k)} aria-pressed={actif}
              style={{ ...card, padding: '14px 16px', textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-sans)', border: `1.5px solid ${actif ? 'var(--color-primary)' : 'var(--border-default)'}` }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: t.rouge && t.v > 0 ? 'var(--red-600)' : 'var(--navy-900)' }}>{t.v}</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginTop: 2 }}>{t.l}</div>
            </button>
          );
        })}
      </div>

      {/* ⚠️ D'OÙ VIENNENT « COUVERT », « PARTIEL » ET « FAIBLE » — dit à l'écran
          (question de la RQ, 01/09/2026 : « comment change-t-il ? ai-je la
          main ? »). La réponse est NON, et son absence était le vrai défaut :
          quatre pastilles de couleur sans origine déclarée se lisent comme un
          verdict de l'outil sur le laboratoire. Ce sont des appréciations
          écrites à la main dans le référentiel, à relire — le drapeau
          « à valider » ci-dessus les compte. */}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '82ch' }}>
        <strong>Couvert · Partiel · Faible · Non couvert</strong> ne se règlent pas à l’écran : ce sont
        des appréciations portées <strong>sur l’outil</strong>, pas sur le laboratoire, et elles sont
        écrites dans le référentiel de correspondance. Elles disent si un écran de la plateforme répond
        à la clause et en produit la trace — pas si vous y répondez. Les faire changer suppose que
        l’écran change : c’est une demande d’évolution, pas un réglage. Il en va de même du texte des
        quatre rubriques de chaque clause, qui engage notre interprétation et porte la mention
        <strong> « à valider »</strong> tant que vous ne l’avez pas relu ligne à ligne.
      </p>

      <FilterBar q={q} onQ={setQ} placeholder="Rechercher un article, un thème, un écran…"
        total={ISO15189.length} affiches={liste.length} onReset={() => { setQ(''); setFiltre('toutes'); }} />

      {DIVERGENCES.length > 0 && (
        <div style={{ ...card, padding: '14px 16px', borderLeft: '3px solid var(--amber-600)' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
            Citations à l’écran qui ne concordent pas avec ce référentiel ({DIVERGENCES.length})
          </h2>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
            Relevé automatique, non corrigé : une référence normative ne se change pas sans
            vérification sur l’exemplaire du laboratoire.
          </p>
          {DIVERGENCES.map((d) => (
            <div key={d.ecran} style={{ padding: '8px 0', borderTop: '1px dashed var(--border-subtle)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
              <strong>{d.ecran}</strong> cite <span style={{ fontFamily: 'var(--font-mono)' }}>§{d.cite}</span>,
              le référentiel dit <span style={{ fontFamily: 'var(--font-mono)' }}>§{d.referentiel}</span>. {d.remarque}
            </div>
          ))}
        </div>
      )}

      {liste.length === 0 ? (
        <EmptyState titre="Aucun article ne correspond." indice="Élargissez la recherche ou retirez le filtre." />
      ) : (
        [4, 5, 6, 7, 8].map((ch) => {
          const duChapitre = liste.filter((c) => c.chapitre === ch);
          if (!duChapitre.length) return null;
          return (
            <section key={ch} style={{ ...card, overflow: 'hidden' }}>
              <div style={{ padding: '11px 16px', background: 'var(--gray-25)', borderBottom: '1px solid var(--border-default)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>Chapitre {ch}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', marginLeft: 10 }}>{CHAPITRES[ch]}</span>
              </div>
              {duChapitre.map((c) => {
                const ton = TONS[c.couverture];
                const deplie = ouverte === c.ref;
                return (
                  <div key={c.ref} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <button onClick={() => setOuverte(deplie ? null : c.ref)} aria-expanded={deplie}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '11px 16px', cursor: 'pointer', fontFamily: 'var(--font-sans)', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', minWidth: 52 }}>§{c.ref}</span>
                      <span style={{ flex: '1 1 220px', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{c.titre}</span>
                      <span style={{ flex: '1 1 180px', fontSize: 12.5, color: 'var(--text-muted)' }}>
                        {c.ecrans.length ? c.ecrans.map((e) => e.label).join(' · ') : '— aucun écran'}
                      </span>
                      {!c.valide && (
                        <span title="Rédaction proposée, non relue par le RQ"
                          style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--gray-50)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '1px 8px' }}>à valider</span>
                      )}
                      <span title={COUVERTURE_META[c.couverture].aide}
                        style={{ fontSize: 11, fontWeight: 700, background: ton.bg, color: ton.fg, borderRadius: 'var(--radius-pill)', padding: '2px 10px', whiteSpace: 'nowrap' }}>
                        {COUVERTURE_META[c.couverture].label}
                      </span>
                      <I n={deplie ? 'x' : 'arrow'} s={13} style={{ color: 'var(--text-muted)' }} />
                    </button>

                    {deplie && (
                      <div style={{ padding: '4px 16px 18px 68px', display: 'grid', gap: 12 }}>
                        {[
                          ['Ce que la norme demande', c.exigence],
                          ['Pourquoi cette exigence existe', c.pourquoi],
                          ['Comment BIO24 y répond', c.chezVous],
                          ['Ce que l’auditeur demandera', c.preuve],
                        ].map(([t, v]) => (
                          <div key={t}>
                            <h3 style={{ margin: '0 0 4px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>{t}</h3>
                            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--text-body)' }}><Texte v={v} /></p>
                          </div>
                        ))}
                        {c.ecrans.length > 0 && (
                          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                            {c.ecrans.map((e, i) => e.module === 'qualite' && e.parametrage ? (
                              /* ⚠️ LE PARAMÉTRAGE N'EST PAS UNE VUE. `go('settings')`
                                 ne rendait rien — le bouton existait, le clic ne
                                 faisait rien (relevé RQ du 01/09/2026 sur §5.1).
                                 Il s'ouvre par `openSettings(onglet)`, et l'onglet
                                 dépose directement là où la clause se règle. */
                              <Bouton variante="primaire" taille="sm" key={i} onClick={() => openSettings(e.parametrage)}>{e.label} →</Bouton>
                            ) : e.module === 'qualite' && e.vue ? (
                              <Bouton variante="primaire" taille="sm" key={i} onClick={() => go(e.vue as GedView)}>{e.label} →</Bouton>
                            ) : (
                              <span key={i} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--gray-50)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 10px' }}>
                                {e.label} · module {e.module === 'manuel' ? 'Manuel' : 'Site'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })
      )}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)', lineHeight: 1.6 }}>
        Le texte de l’ISO 15189 n’est pas reproduit : c’est un document protégé par le droit
        d’auteur. Ce tableau donne les références et l’interprétation du laboratoire ;
        l’exemplaire acheté reste la seule source opposable.
      </p>
    </div>
  );
}
